<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const modelData = ref(null);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const modelId = ref('');
const displayValue = ref('');

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    filteredOptions.value = modelData.value.filter((option) => option.model_name.toLowerCase().includes(searchQuery.value.toLowerCase()) || option.brand.toLowerCase().includes(searchQuery.value.toLowerCase()));
};

onMounted(async () => {
    await Promise.all([searchModel()]);
    nextTick(() => {
        filteredOptions.value = modelData.value;
    });
});

const searchModel = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/vehicle-model/select-box', config, {
            immediate: false,
            onSuccess(response) {
                const newList = response.data.data.map((item) => ({
                    value: item.vehicle_model_id,
                    origin: item.vehicle_origin || '',
                    brand: item.vehicle_brand || '',
                    type: item.vehicle_type || '',
                    grade: item.vehicle_grade || '',
                    model_name: item.vehicle_model_name || ''
                }));
                modelData.value = newList || [];
                //console.log(modelData.value);
            },
            onError(err) {
                console.error('Error:', err.message);
                modelData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        modelData.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (modelData.value && modelData.value.length > 0) {
            const matchedOption = modelData.value.find((option) => option.value === newVal);
            if (matchedOption) {
                displayValue.value = matchedOption.model_name;
                inputValue.value = matchedOption.value;
            } else {
                displayValue.value = '';
                inputValue.value = newVal;
            }
        } else {
            displayValue.value = '';
            inputValue.value = newVal;
        }
    },
    { immediate: true }
);

watch(
    modelData,
    (newmodelData) => {
        if (!newmodelData || newmodelData.length === 0) return;

        const matchedOption = newmodelData.find((option) => option.value === props.modelValue);
        if (matchedOption) {
            displayValue.value = matchedOption.garage_name;
            inputValue.value = matchedOption.value;
        }
    },
    { immediate: true, deep: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서 위치 조정
    // if (lengthDiff !== 0) {
    //     cursorPosition = cursorPosition + lengthDiff;
    // }

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value,
        origin: '',
        brand: '',
        type: '',
        grade: '',
        model_name: ''
    });
};

// 팝업 열기/닫기
const togglePopup = () => {
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

// 값 선택
const selectValue = (option) => {
    inputValue.value = option.text;
    displayValue.value = option.model_name;
    //emitUpdate(option.text);

    emit('update:modelValue', option.value);
    emit('input', {
        id: props.inputId,
        value: option.value,
        origin: option.origin || '',
        brand: option.brand || '',
        type: option.type || '',
        grade: option.grade || '',
        model_name: option.model_name || ''
    });

    modelId.value = option.value;
    closePopup();
};

const clearInput = () => {
    displayValue.value = '';
    modelId.value = '';
    emitUpdate('');
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filterOptions();
    }
});

defineExpose({
    modelId
});
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <input
                v-if="visible"
                type="text"
                :id="inputId"
                v-model="displayValue"
                class="block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': button,
                    'error-border': ValidationMsg,
                    'readonly-Bg': isReadonly
                }"
                readonly
                required
                :title="inputValue"
                :placeholder="placeholder"
                :required="required"
                @input="onInput"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button mr-[calc(6.5rem)]">&times;</button>

                <button v-if="!isReadonly" @click="togglePopup" class="button-style">검색</button>
            </div>
        </div>

        <div v-if="isPopupVisible" class="popup-overlay">
            <div class="popup-content">
                <!-- <h3>선택</h3> -->
                <input v-model="searchQuery" @input="filterOptions" placeholder="모델을 검색하세요" class="search-input" style="margin-top: 1rem" />
                <div class="options-container">
                    <table class="model-grid">
                        <thead>
                            <tr>
                                <th style="display: none">모델id</th>
                                <th>차량구분</th>
                                <th>브랜드</th>
                                <th>차종</th>
                                <th>등급</th>
                                <th>모델</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                <td style="display: none">{{ option.value }}</td>
                                <td>{{ option.origin }}</td>
                                <td>{{ option.brand }}</td>
                                <td>{{ option.type }}</td>
                                <td>{{ option.grade }}</td>
                                <td>{{ option.model_name }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <button @click="closePopup" class="close-button">닫기</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.caution-customer {
    color: #ff0000;
    font-weight: bold;
}

.error-border {
    border: 1px solid red !important;
}
.readonly-Bg {
    background-color: whitesmoke !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 1.4rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    text-align: left;
    min-width: 55rem;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 40rem;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.model-grid {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.model-grid th,
.model-grid td {
    border: 0.1rem solid #ddd;
    border-left: none;
    border-right: none;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    text-align: center;
}

.model-grid th {
    background-color: #f2f2f2;
}

.model-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.model-grid th:hover {
    cursor: default;
}

.model-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
